import { OreNavBarContent, OreNavBarVertical } from '@edenredespana/oreneta'
import { useTranslation } from 'react-i18next'

import {
  SidebarLink,
  SidebarSection,
  useManagedNavigation,
  useSidebar,
  useUI,
} from '@/Shared/ui/navigation'
import { UserType } from '@/User/domain'
import { useUserData } from '@/User/ui/context'
import { navigationTranslation } from '@/Shared/ui/navigation/navigationTranslation'

export interface SidebarItemModel {
  title?: string
  icon?: JSX.Element
  pillText?: string | JSX.Element
  link?: string
  childs?: SidebarItemModel[] // Means is Parent
  userTypeExclusive?: UserType[]
}

export interface SidebarSectionModel {
  title?: string
  items?: SidebarItemModel[]
}

export type SidebarMenuItemModel = SidebarSectionModel & SidebarItemModel

const Sidebar = (): JSX.Element => {
  const { sidebarCollapsed } = useUI()
  const { t } = useTranslation()
  const { hover, handleMouseEnter, handleMouseLeave } = useSidebar()
  const { userType, merchant } = useUserData()
  const { getSidebarMenu } = useManagedNavigation(merchant?.isNursery || false)

  const filterMenuItems = (
    menuItems: SidebarMenuItemModel[],
    userType: UserType | null,
  ): SidebarMenuItemModel[] => {
    if (!userType) return menuItems

    return menuItems.reduce((acc: SidebarMenuItemModel[], menuItem) => {
      if (menuItem.userTypeExclusive && !menuItem.userTypeExclusive.includes(userType)) {
        return acc
      }

      // Hide "Local" (store) and "Información de pago" (contracts) sections for master groups
      if (
        merchant?.isMasterGroup &&
        (menuItem.title === navigationTranslation.sidebar.sections.store ||
          menuItem.title === navigationTranslation.sidebar.sections.contracts)
      ) {
        return acc
      }

      const newItem = { ...menuItem }

      if (newItem.items) {
        newItem.items = filterMenuItems(newItem.items, userType)
        // If after filtering there are no items, skip rendering this section
        if (newItem.items.length === 0) {
          return acc
        }
      }
      if (newItem.childs) {
        newItem.childs = filterMenuItems(newItem.childs, userType)
      }

      acc.push(newItem)
      return acc
    }, [])
  }

  return (
    <OreNavBarVertical
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      hover={hover}
      height="calc(100vh - 64px)"
      collapse={sidebarCollapsed}
    >
      <OreNavBarContent>
        {filterMenuItems(getSidebarMenu(), userType).map(
          (menuItem: SidebarMenuItemModel, index: number) => {
            if (menuItem.items) {
              return (
                <SidebarSection
                  title={t(menuItem.title || '') || ''}
                  items={menuItem.items}
                  hover={hover}
                  key={index}
                />
              )
            }
            return (
              <SidebarLink
                title={t(menuItem.title || '')}
                icon={menuItem.icon || <></>}
                link={menuItem.link}
                hover={hover}
                key={index}
              />
            )
          },
        )}
      </OreNavBarContent>
    </OreNavBarVertical>
  )
}

export { Sidebar }
